// Champ « tableau de lignes » du formulaire dynamique : lignes ajoutées et retirées, colonnes
// texte, nombre, montant, date, choix unique ou oui-non, erreurs par cellule (chemin ligne.colonne).
import type { Champ, Colonne } from '@cc/contrats';
import { Bouton, Champ as ChampSaisie, DateChamp, Selection } from '@cc/ui';
import { CircleAlert } from 'lucide-react';
import type { ErreurSaisie } from './formulaire-depuis-fiche';
import styles from './depot.module.css';

type Ligne = Record<string, unknown>;

function Cellule({ id, colonne, valeur, onChange, erreur, desactive }: { id: string; colonne: Colonne; valeur: unknown; onChange: (v: unknown) => void; erreur: string | null; desactive?: boolean }) {
  const commun = { id, libelle: colonne.libelle, erreur, required: colonne.obligatoire, disabled: desactive };
  switch (colonne.type) {
    case 'nombre':
    case 'montant':
      return (
        <ChampSaisie
          {...commun}
          type="number"
          inputMode={colonne.type === 'montant' ? 'numeric' : 'decimal'}
          step={colonne.type === 'montant' ? 1 : 'any'}
          min={colonne.type === 'montant' ? 0 : colonne.validation?.min}
          max={colonne.validation?.max}
          value={valeur === undefined || valeur === null ? '' : String(valeur)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value))}
          aide={colonne.type === 'montant' ? 'En francs CFA' : undefined}
        />
      );
    case 'date':
      return <DateChamp {...commun} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value || undefined)} min={colonne.validation?.date_min} max={colonne.validation?.date_max} />;
    case 'choix_unique':
      return <Selection {...commun} optionVide="Choisir" options={(colonne.options ?? []).map((o) => ({ valeur: o.valeur, libelle: o.libelle }))} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value || undefined)} />;
    case 'oui_non':
      return (
        <Selection
          {...commun}
          optionVide="Choisir"
          options={[
            { valeur: 'oui', libelle: 'Oui' },
            { valeur: 'non', libelle: 'Non' },
          ]}
          value={valeur === true ? 'oui' : valeur === false ? 'non' : ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value === 'oui')}
        />
      );
    default:
      return <ChampSaisie {...commun} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChange(e.target.value)} maxLength={colonne.validation?.longueur_max ?? 255} />;
  }
}

export interface ProprietesChampTableau {
  id: string;
  champ: Champ;
  valeur: unknown;
  onChange: (v: Ligne[] | undefined) => void;
  erreur: string | null;
  /** Erreurs des cellules, chemin « indice.colonne ». */
  erreursSous: ErreurSaisie[];
  desactive?: boolean;
}

export function ChampTableau({ id, champ, valeur, onChange, erreur, erreursSous, desactive }: ProprietesChampTableau) {
  const lignes: Ligne[] = Array.isArray(valeur) ? (valeur as Ligne[]) : [];
  const colonnes = champ.colonnes ?? [];
  const max = champ.validation?.max ?? 500;
  const modifier = (i: number, code: string, v: unknown) => {
    const suivantes = lignes.map((l, j) => (j === i ? { ...l, [code]: v } : l));
    onChange(suivantes);
  };
  return (
    <fieldset className={styles.groupe} id={id} aria-describedby={erreur ? `${id}-erreur` : undefined}>
      <legend>
        {champ.libelle}
        {champ.obligatoire ? (
          <span className={styles.obligatoire} aria-hidden="true">
            *
          </span>
        ) : (
          <span className={styles.facultatif}>(facultatif)</span>
        )}
      </legend>
      {champ.aide ? <p className={styles.aide}>{champ.aide}</p> : null}
      {lignes.length === 0 ? <p className={styles.aide}>Aucune ligne pour l&apos;instant.</p> : null}
      <ol className={styles.lignes}>
        {lignes.map((l, i) => (
          <li key={i} className={styles.ligneTableau}>
            <div className={styles.enteteLigne}>
              <strong>Ligne {i + 1}</strong>
              <Bouton variante="discret" petit disabled={desactive} onClick={() => onChange(lignes.filter((_, j) => j !== i))} aria-label={`Retirer la ligne ${i + 1}`}>
                Retirer
              </Bouton>
            </div>
            <div className={styles.sousChamps}>
              {colonnes.map((k) => (
                <Cellule
                  key={k.code}
                  id={`${id}-${i}-${k.code}`}
                  colonne={k}
                  valeur={l[k.code]}
                  onChange={(v) => modifier(i, k.code, v)}
                  erreur={erreursSous.find((e) => e.champ === `${i}.${k.code}`)?.message ?? null}
                  desactive={desactive}
                />
              ))}
            </div>
          </li>
        ))}
      </ol>
      {erreur ? (
        <span id={`${id}-erreur`} className={styles.erreur}>
          <CircleAlert aria-hidden strokeWidth={1.75} />
          {erreur}
        </span>
      ) : null}
      <div>
        <Bouton variante="secondaire" petit disabled={desactive || lignes.length >= max} onClick={() => onChange([...lignes, {}])}>
          Ajouter une ligne
        </Bouton>
      </div>
    </fieldset>
  );
}
